import React, { ComponentType } from 'react';
import { FormStateBasic } from 'informed';
import { FormsRefreshTokenProvider } from 'libs/shared/data-access/src/hooks/useFormsRefreshToken';

import {
  AccountsRefreshAuthTokenProvider,
  CollegePredictorRefreshAuthTokenProvider,
  PaymentsRefreshAuthTokenProvider
} from '@nw/shared/data-access';
import { SUCCESS } from '@nw/shared/utils';

import { CustomClassNamesInterface } from '../../../shared/types/MiscellaneousTypes';

import {
  usePublicFormsMachine,
  UsePublicMachineReturnType
} from '../../hooks/usePublicFormsMachine';
import {
  PublicFormMachineSendType,
  PublicFormMachineStateType
} from '../../machines/PublicFormsMachine/types';
import { DefaultFormTypes } from '../../types/ProductTypes';

interface FormMachineProps {
  publicFormsMachineSend: PublicFormMachineSendType;
  publicFormsMachineState: PublicFormMachineStateType;
  status: UsePublicMachineReturnType['status'];
  errors: UsePublicMachineReturnType['errors'];
}

interface WithPublicFormsMachineProps {
  formId: string;
  localFormConfig?: DefaultFormTypes;
  isPublicForm?: boolean;
  clientId?: string;
  customHeader?: () => React.ReactElement;
  onSuccessInitialConfig?: () => void;
  renderCustomSuccessPage?: (
    formValues: FormStateBasic['values'],
    publicFormMachineState: PublicFormMachineStateType,
    publicFormMachineSend: PublicFormMachineSendType
  ) => React.ReactElement;
  customClassNames: CustomClassNamesInterface;
}

const withProviders = (WrappedComponent: ComponentType<any>) => {
  const WithProviders = (props: any): React.ReactElement => {
    return (
      <AccountsRefreshAuthTokenProvider>
        <PaymentsRefreshAuthTokenProvider>
          <FormsRefreshTokenProvider>
            <CollegePredictorRefreshAuthTokenProvider>
              <WrappedComponent {...props} />
            </CollegePredictorRefreshAuthTokenProvider>
          </FormsRefreshTokenProvider>
        </PaymentsRefreshAuthTokenProvider>
      </AccountsRefreshAuthTokenProvider>
    );
  };

  return WithProviders;
};

const withPublicFormsMachine = (
  WrappedComponent: ComponentType<
    WithPublicFormsMachineProps & FormMachineProps
  >
) => {
  const WithPublicFormsMachine = (
    props: WithPublicFormsMachineProps
  ): React.ReactElement => {
    const { formId, localFormConfig, isPublicForm = false, clientId } = props;
    const {
      errors,
      status,
      publicFormsMachineState,
      publicFormsMachineSend
    }: UsePublicMachineReturnType = usePublicFormsMachine({
      formId: formId,
      localProductConfig: localFormConfig,
      isPublicForm,
      clientId
    });
    if (localFormConfig) {
      status.getInitialConfigStatus = SUCCESS;
    }
    return (
      <WrappedComponent
        {...props}
        publicFormsMachineSend={publicFormsMachineSend}
        publicFormsMachineState={publicFormsMachineState}
        status={status}
        errors={errors}
      />
    );
  };

  return withProviders(WithPublicFormsMachine);
};

export default withPublicFormsMachine;
